/**
 * Workshop Memory View
 *
 * Main container for temporal intelligence features:
 * - Visual Timeline Navigator
 * - Memory Summaries
 * - Proof Trails
 * - Activity Peaks
 *
 * Voice triggers:
 * - "What did we do in March?"
 * - "Show me the timeline"
 * - "When did rust remediation start?"
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

import { Timeline, TimelineMini } from './Timeline';
import { ProofTrailView, ProofTrailCard, ProofSearchResult } from './ProofTrail';
import { workshopMemory } from '@/services/memory';
import { demoEvents, demoSummaries, demoAnchors, demoProofTrails } from '@/data/demoEvents';

import type { TimelineConfig, TimelineSegment, TemporalEvent, MemorySummary, MemoryAnchor, ProofTrail } from '@/types/memory';
import type { ImageMetadata } from '@/types';

interface MemoryViewProps {
  vehicleId: string;
  images: ImageMetadata[];
  initialQuery?: string;
  initialTimeRange?: { relative?: string };
  initialProofTarget?: string;
  onImageClick: (imageId: string) => void;
  onClose: () => void;
  className?: string;
}

export function MemoryView({
  vehicleId,
  images,
  initialQuery,
  initialTimeRange,
  initialProofTarget,
  onImageClick,
  onClose,
  className,
}: MemoryViewProps) {
  // Timeline config
  const [timelineConfig, setTimelineConfig] = useState<TimelineConfig>({
    scale: 'month',
    groupBy: 'phase',
    showGaps: true,
    showAnchors: true,
    showSummaries: true,
  });

  // Selected period/summary
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [selectedProofTrail, setSelectedProofTrail] = useState<ProofTrail | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<'timeline' | 'proof'>('timeline');

  // Build image map for lookups
  const imageMap = useMemo(() => {
    const map = new Map<string, ImageMetadata>();
    images.forEach(img => map.set(img.id, img));
    return map;
  }, [images]);

  // Get events for this vehicle (demo data for now)
  const events = useMemo(() => {
    return demoEvents.filter(e => e.vehicleId === vehicleId);
  }, [vehicleId]);

  // Build timeline segments
  const segments = useMemo(() => {
    return workshopMemory.buildTimelineSegments(events, timelineConfig);
  }, [events, timelineConfig]);

  // Get summaries
  const summaries = useMemo(() => {
    return demoSummaries.filter(s => s.vehicleId === vehicleId);
  }, [vehicleId]);

  // Get anchors
  const anchors = useMemo(() => {
    return demoAnchors.filter(a => a.vehicleId === vehicleId);
  }, [vehicleId]);

  // Get proof trails
  const proofTrails = useMemo(() => {
    return demoProofTrails.filter(p => p.vehicleId === vehicleId);
  }, [vehicleId]);

  // Filter summaries by time range if provided
  const filteredSummaries = useMemo(() => {
    if (!initialTimeRange?.relative) return summaries;

    const relative = initialTimeRange.relative.toLowerCase();
    return summaries.filter(s => {
      const label = s.periodLabel.toLowerCase();
      return label.includes(relative);
    });
  }, [summaries, initialTimeRange]);

  // Handle initial proof target
  useEffect(() => {
    if (initialProofTarget) {
      setViewMode('proof');
      // Try to find a matching proof trail
      const target = initialProofTarget.toLowerCase();
      const match = proofTrails.find(p =>
        p.title.toLowerCase().includes(target) ||
        p.component.toLowerCase().includes(target)
      );
      if (match) {
        setSelectedProofTrail(match);
      }
    }
  }, [initialProofTarget, proofTrails]);

  // Handle segment click
  const handleSegmentClick = useCallback((segment: TimelineSegment) => {
    setSelectedPeriod(segment.id);
  }, []);

  // Handle event click
  const handleEventClick = useCallback((event: TemporalEvent) => {
    if (event.primaryImageId) {
      onImageClick(event.primaryImageId);
    }
  }, [onImageClick]);

  // Handle anchor click
  const handleAnchorClick = useCallback((anchor: MemoryAnchor) => {
    if (anchor.primaryImageId) {
      onImageClick(anchor.primaryImageId);
    }
  }, [onImageClick]);

  // Handle proof trail view
  const handleProofTrailClick = useCallback((trail: ProofTrail) => {
    setSelectedProofTrail(trail);
  }, []);

  // Get the matching summary for display
  const displaySummary = useMemo(() => {
    if (filteredSummaries.length > 0) {
      return filteredSummaries[0];
    }
    return null;
  }, [filteredSummaries]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={clsx('fixed inset-0 z-40 bg-workshop-bg overflow-hidden flex flex-col', className)}
    >
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-workshop-border bg-workshop-surface/50 backdrop-blur">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-workshop-surface transition-colors"
          >
            <BackIcon className="w-5 h-5 text-workshop-text-muted" />
          </button>

          <div>
            <h1 className="text-display-sm text-white font-semibold">Workshop Memory</h1>
            <p className="text-workshop text-workshop-text-muted">
              {initialQuery || 'Temporal Intelligence'}
            </p>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex rounded-lg bg-workshop-surface p-1">
          <button
            onClick={() => setViewMode('timeline')}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              viewMode === 'timeline'
                ? 'bg-workshop-accent text-white'
                : 'text-workshop-text-muted hover:text-white'
            )}
          >
            Timeline
          </button>
          <button
            onClick={() => setViewMode('proof')}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              viewMode === 'proof'
                ? 'bg-workshop-accent text-white'
                : 'text-workshop-text-muted hover:text-white'
            )}
          >
            Proof Trails
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <AnimatePresence mode="wait">
          {viewMode === 'timeline' ? (
            <motion.div
              key="timeline"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="p-6 max-w-4xl mx-auto"
            >
              {/* Timeline Component - month by month */}
              <Timeline
                segments={segments}
                events={events}
                anchors={anchors}
                config={timelineConfig}
                onConfigChange={setTimelineConfig}
                onSegmentClick={handleSegmentClick}
                onEventClick={handleEventClick}
                onAnchorClick={handleAnchorClick}
              />
            </motion.div>
          ) : (
            <motion.div
              key="proof"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-6"
            >
              {/* Proof Trails Grid */}
              <h3 className="text-lg font-semibold text-white mb-4">Evidence Chains</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {proofTrails.map(trail => (
                  <ProofTrailCard
                    key={trail.id}
                    trail={trail}
                    onClick={() => handleProofTrailClick(trail)}
                  />
                ))}
              </div>

              {proofTrails.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-workshop-text-muted">No proof trails available for this vehicle.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Proof Trail Modal */}
      <AnimatePresence>
        {selectedProofTrail && (
          <ProofTrailView
            trail={selectedProofTrail}
            images={imageMap}
            onStepClick={(step) => step.primaryImageId && onImageClick(step.primaryImageId)}
            onImageClick={onImageClick}
            onClose={() => setSelectedProofTrail(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

export default MemoryView;
